import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Link, useParams, useNavigate } from "react-router-dom";
import { editActivity, getActivity } from "../store/slices/activity";
import { nameregex } from "../helper/regex";
import { GrUploadOption } from "react-icons/gr";
import { API_BASE_URL, API_PATHS } from "../utils/constants/api.constants";

const EditActivities = () => {
  const { id } = useParams();
  const user = JSON.parse(localStorage.getItem("users") || "null");
  const token = user?.data?.accessToken || "";
  const [name, setName] = useState("");
  const [activityDate, setactivityDate] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState([]);
  const [initialImages, setInitialImages] = useState([]); // <-- store original images
  const [imagesChanged, setImagesChanged] = useState(false); // <-- track changes
  const [imageError, setImageError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isDisabled, setIsDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  useEffect(() => {
    const fetchactivityDetails = async () => {
      try {
        const item = { id };
        const data = await dispatch(getActivity(item)).unwrap();
        const activity = data?.data?.[0];

        if (activity) {
          setName(activity.name || "");
          setactivityDate(formatDateForInput(activity.activityDate));
          setDescription(activity.description || "");
          const imgs = Array.isArray(activity.image) ? activity.image : [];
          setImages(imgs);
          setInitialImages(imgs); // <-- save originals for later
        }
      } catch (error) {
        console.error(
          "Failed to fetch activity details:",
          error?.message || error,
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchactivityDetails();
  }, [dispatch, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const validationErrors = { ...errors };

    switch (name) {
      case "name":
        setName(value);
        validationErrors[name] =
          value.trim() === "" || !nameregex.test(value)
            ? "Please enter a valid name"
            : "";
        break;

      case "description":
        setDescription(value);
        validationErrors[name] =
          value.trim() === "" || !nameregex.test(value)
            ? "Please enter a valid description"
            : "";
        break;

      case "activityDate":
        setactivityDate(value);
        validationErrors[name] =
          value.trim() === "" ? "Please enter activity date" : "";
        break;

      default:
        break;
    }

    setErrors(validationErrors);
  };

  const imageUpload = async (e) => {
    setImageError("");
    const files = Array.from(e.target.files || []);

    if (files.length === 0) {
      setImageError("Please select at least one file to upload");
      return;
    }
    if (images.length + files.length > 10) {
      setImageError("You can upload a maximum 10 images");
      return;
    }

    const newImage = [];
    for (let file of files) {
      if (file.size > 5 * 1024 * 1024) {
        setImageError("Please upload image less than 5 MB");
        return;
      }

      const formData = new FormData();
      formData.append("file", file);

      const requestOptions = {
        method: "POST",
        body: formData,
        headers: {
          "x-access-token": token,
        },
      };

      try {
        const response = await fetch(
          `${API_BASE_URL}${API_PATHS.upload}`,
          requestOptions,
        );

        if (!response.ok) {
          throw new Error(`Upload failed with status: ${response.status}`);
        }

        const data = await response.json();

        if (data?.status === 1 && data?.data?.location) {
          newImage.push({
            name: file.name,
            mediaLink: data.data.location,
            isActive: true,
          });
        } else {
          throw new Error(data?.message || "Failed to upload image");
        }
      } catch (error) {
        setImageError(
          error?.message || "Something went wrong while uploading the image",
        );
        return;
      }
    }

    setImages((prev) => {
      const merged = [...prev, ...newImage];
      return merged;
    });

    setImagesChanged(true); // <-- user added new images
    e.target.value = null;
  };

  const removeImage = (index) => {
    setImages((prevImage) => {
      const updatedImage = [...prevImage];
      updatedImage.splice(index, 1);

      if (updatedImage.length === 0) {
        setImageError("Please upload at least one image");
      } else {
        setImageError("");
      }

      return updatedImage;
    });

    setImagesChanged(true); // <-- user removed an image
  };

  const Editactivitys = async (e) => {
    e.preventDefault();
    setIsDisabled(true);
    setTimeout(() => setIsDisabled(false), 3000);

    const validationErrors = {};
    if (!name.trim()) {
      validationErrors.name = "Please enter name";
    } else if (!nameregex.test(name)) {
      validationErrors.name = "Name must be at least three characters";
    }

    if (!description.trim()) {
      validationErrors.description = "Please enter a valid description";
    }

    if (!activityDate.trim()) {
      validationErrors.activityDate = "Please enter activity date";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // If user didn't change images, send the original images (initialImages).
    // If they did change, send the current images array.
    const imagesToSend = imagesChanged ? images : initialImages;

    const item = {
      id,
      name,
      description,
      image: imagesToSend, // full objects expected by backend (mediaLink, name, isActive)
      activityDate,
    };

    try {
      await dispatch(editActivity(item)).unwrap();
      navigate("/admin/activity");
      alert("Activity details edited successfully!");
    } catch (error) {
      alert(error?.message || error);
    }
  };

  const cancel = () => {
    navigate(-1);
  };

  if (isLoading) {
    return <p>Loading...</p>;
  }

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
        <h6 className="fw-semibold mb-0">Edit Activity</h6>
        <ul className="d-flex align-items-center gap-2">
          <li className="fw-medium">
            <Link
              to="/admin/activity"
              className="d-flex align-items-center gap-1 hover-text-primary"
            >
              Activity
            </Link>
          </li>
          <li> &gt; </li>
          <li className="fw-medium">Edit Activity</li>
        </ul>
      </div>
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          <form onSubmit={Editactivitys}>
            <div className="row mb-40">
              <div className="col-md-5">
                <label className="form-label">
                  Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={name}
                  onChange={handleChange}
                  className={`form-control ${errors.name ? "is-invalid" : ""}`}
                  placeholder="Enter activity Name"
                />
                {errors.name && (
                  <div className="text-danger mt-2">{errors.name}</div>
                )}
              </div>

              <div className="col-md-5">
                <label className="form-label">
                  Date of Activity <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  name="activityDate"
                  value={activityDate}
                  onChange={handleChange}
                  className={`form-control ${
                    errors.activityDate ? "is-invalid" : ""
                  }`}
                  placeholder="Enter activity date"
                />
                {errors.activityDate && (
                  <div className="text-danger mt-2">{errors.activityDate}</div>
                )}
              </div>

              <div className="col-md-10 mt-3">
                <label className="form-label">
                  Images <span className="text-danger">*</span>
                </label>
                <br />
                <label htmlFor="fileInput" className="custom-button">
                  <GrUploadOption
                    style={{ fontSize: "16px", lineHeight: "18px" }}
                  />
                  &nbsp; Upload Image
                </label>
                <input
                  type="file"
                  id="fileInput"
                  name="image"
                  accept="image/*"
                  onChange={imageUpload}
                  className={`form-control ${imageError ? "is-invalid" : ""}`}
                  multiple
                />
                {imageError && (
                  <div className="text-danger mt-2">{imageError}</div>
                )}
                <br />
                <label className="form-label mt-3">Current Images</label>
                <div className="row">
                  {images.map((img, index) => (
                    <div
                      className="col-md-2 mb-2 position-relative"
                      key={index}
                    >
                      <img
                        src={img.mediaLink}
                        style={{ height: "120px", objectFit: "cover" }}
                        alt={img.name || ""}
                        className="img-thumbnail"
                      />
                      <button
                        className="image-remove-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          removeImage(index);
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="col-md-10 mt-3">
                <label className="form-label">
                  Description <span className="text-danger">*</span>
                </label>
                <textarea
                  name="description"
                  value={description}
                  className={`form-control ${
                    errors.description ? "is-invalid" : ""
                  }`}
                  onChange={handleChange}
                />
                {errors.description && (
                  <div className="text-danger mt-2">{errors.description}</div>
                )}
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-center gap-3">
              <button
                type="button"
                onClick={cancel}
                className="btn btn-outline-primary-600 wd-100 radius-8 px-14 py-6 text-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isDisabled}
                className="btn btn-primary-600 wd-100 radius-8 px-14 py-6 text-md"
              >
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditActivities;
